import { Drawer, useTheme } from '@mui/material';
import { useState } from 'react';

import Location from '@/components/GlobalNavigation/Location/Location';
import Icon, { IconName } from '@/components/Icon/Icon';
import { userLocationInterface } from '@/hooks/geo';
import useViewPortSize from '@/hooks/useViewportSize';
import { brandColors } from '@/styles/sunrunTheme.styles';
import { trackEvents } from '@/utils/analytics';
import usStates from '@/utils/usStates';

import useStyles from './LocationDisplay.styles';

export interface LocationDisplayProps extends userLocationInterface {
  isOnDark: boolean;
  onClick?: () => void;
  className?: string;
}

const LocationDisplay = ({
  userLocation,
  setUserLocation,
  isOnDark,
  onClick,
  className,
}: LocationDisplayProps) => {
  const { classes, cx } = useStyles();
  const locationClass = cx(
    classes.location,
    isOnDark && classes.locationDark,
    className
  );

  const [locationDrawerOpen, setLocationDrawerOpen] = useState<boolean>(false);
  const { isMobile } = useViewPortSize();
  const { typography } = useTheme();
  const userState = userLocation && userLocation.state;

  return (
    <>
      <div
        className={locationClass}
        data-testid='locationDisplay'
        role='button' // Add role
        tabIndex={0} // Make it focusable
        aria-label='Open location drawer' // Add accessible label
        onClick={() => {
          if (onClick) {
            onClick();
          }
          setLocationDrawerOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            if (onClick) {
              onClick();
            }
            setLocationDrawerOpen(true);
          }
        }}
      >
        <Icon name={IconName.PIN} />
        {userState && userState in usStates
          ? `State: ${usStates[userState]}`
          : 'Enter your location'}
      </div>
      <div>
        <Drawer
          open={locationDrawerOpen}
          anchor={isMobile ? 'right' : 'left'}
          onClose={() => {
            setLocationDrawerOpen(false);
          }}
          PaperProps={{
            style: {
              width: isMobile ? '100%' : 'unset',
              borderRadius: `0 ${typography.pxToRem(10)} ${typography.pxToRem(
                10
              )} 0`,
            },
          }}
          // https://github.com/mui/material-ui/issues/33004#issuecomment-2073819443
          disableRestoreFocus
        >
          <div className={classes.drawerLocationContainer}>
            <div
              className={cx(
                classes.locationHeader,
                isOnDark && classes.locationHeaderDark
              )}
            >
              <div
                role='button' // Add role
                tabIndex={0} // Make it focusable
                aria-label='Close location drawer' // Add accessible label
                onClick={() => {
                  trackEvents([
                    {
                      event: 'engagement',
                      id: 'location-menu-close',
                    },
                  ]);
                  setLocationDrawerOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    trackEvents([
                      {
                        event: 'engagement',
                        id: 'location-menu-close',
                      },
                    ]);
                    setLocationDrawerOpen(false);
                  }
                }}
                className={classes.locationCloseContainer}
              >
                <Icon
                  name={IconName.CHEVRON_LEFT}
                  color={isOnDark ? brandColors.white : brandColors.primaryMain}
                  width={18}
                />
              </div>
              <h3>Update Location</h3>
            </div>
            <Location
              isOnDark={isOnDark}
              closeLocation={() => {
                setLocationDrawerOpen(false);
              }}
              setUserLocation={setUserLocation}
              userLocation={userLocation}
            />
          </div>
        </Drawer>
      </div>
    </>
  );
};

export default LocationDisplay;
